/**
 * Exterior3DViewer — V14.63 Sprint 26G (vanilla three.js rewrite).
 *
 * True 3D orbitable exterior view (terrain + façade extrusion + 6 camera
 * presets + PNG capture). Uses vanilla three.js via the `useThreeViewer`
 * hook — no R3F / drei — to bypass the Emergent dev preview JSX
 * source-tracking attribute injection that crashes R3F's applyProps.
 */
import { useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import {
  Camera as CameraIcon, Mountain, Compass, RotateCcw, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useThreeViewer, disposeGroup } from './useThreeViewer';

const VIEW_PRESETS = {
  aerial:  { label: 'Aérien',     az: 0,    el: 80 },
  facade:  { label: 'Façade',     az: 0,    el: 8  },
  ne:      { label: 'Nord-Est',   az: 45,   el: 35 },
  nw:      { label: 'Nord-Ouest', az: -45,  el: 35 },
  se:      { label: 'Sud-Est',    az: 135,  el: 35 },
  sw:      { label: 'Sud-Ouest',  az: -135, el: 35 },
};

const TERRAIN_COLOR  = 0x365314;
const BUILDING_COLOR = 0xcbd5e1;
const ROOF_COLOR     = 0x475569;
const BASEMENT_COLOR = 0x1f2937;
const FALLBACK_GROUND = 0x1f2a18;

const polyToShape2D = (pointsXZ) => {
  const pts = (pointsXZ || []).map(([x, z]) => new THREE.Vector2(x, z))
    .filter((v) => Number.isFinite(v.x) && Number.isFinite(v.y));
  if (pts.length < 3) return null;
  return new THREE.Shape(pts);
};

const buildExterior = ({ footprintShape, terrainShape, aboveH, belowH, radius }) => {
  const root = new THREE.Group();
  if (terrainShape) {
    const mesh = new THREE.Mesh(
      new THREE.ShapeGeometry(terrainShape),
      new THREE.MeshStandardMaterial({ color: TERRAIN_COLOR, roughness: 0.95 }),
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = -0.02;
    mesh.receiveShadow = true;
    root.add(mesh);
  } else {
    const mesh = new THREE.Mesh(
      new THREE.PlaneGeometry(radius * 4, radius * 4),
      new THREE.MeshStandardMaterial({ color: FALLBACK_GROUND, roughness: 0.95 }),
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.y = -0.05;
    mesh.receiveShadow = true;
    root.add(mesh);
  }

  if (footprintShape && aboveH > 0) {
    const building = new THREE.Mesh(
      new THREE.ExtrudeGeometry(footprintShape, { depth: aboveH, bevelEnabled: false }),
      new THREE.MeshStandardMaterial({ color: BUILDING_COLOR, roughness: 0.6, metalness: 0.05 }),
    );
    building.rotation.x = -Math.PI / 2;
    building.castShadow = true;
    building.receiveShadow = true;
    root.add(building);
    const roof = new THREE.Mesh(
      new THREE.ShapeGeometry(footprintShape),
      new THREE.MeshStandardMaterial({ color: ROOF_COLOR, roughness: 0.55 }),
    );
    roof.rotation.x = -Math.PI / 2;
    roof.position.y = aboveH + 0.001;
    root.add(roof);
  }

  if (footprintShape && belowH > 0) {
    const basement = new THREE.Mesh(
      new THREE.ExtrudeGeometry(footprintShape, { depth: belowH, bevelEnabled: false }),
      new THREE.MeshStandardMaterial({
        color: BASEMENT_COLOR, roughness: 0.8, transparent: true, opacity: 0.6,
      }),
    );
    basement.rotation.x = -Math.PI / 2;
    basement.position.y = -belowH;
    root.add(basement);
  }

  return root;
};

// Add a simple sky-like radial gradient via fog / background.
const applySkyBackground = (scene) => {
  scene.background = new THREE.Color(0x0b1220);
  scene.fog = new THREE.Fog(0x0b1220, 100, 800);
};

const Exterior3DViewer = ({
  exteriorPayload,
  title = 'Vue extérieure 3D',
  subtitle = '',
  onCapture,
  className = '',
  testId = 'exterior-3d-viewer',
}) => {
  const stateRef = useRef(null);
  const [presetKey, setPresetKey] = useState('ne');
  const [capturing, setCapturing] = useState(false);

  const footprintPx = exteriorPayload?.footprint?.footprint_px || [];
  const terrainPx   = exteriorPayload?.terrain?.terrain_px   || [];
  const scaleFp     = exteriorPayload?.footprint?.scale_m_per_px || 0;
  const scaleTr     = exteriorPayload?.terrain?.scale_m_per_px   || 0;
  const aboveH      = exteriorPayload?.extrusion?.above_ground_height_m || 0;
  const belowH      = exteriorPayload?.extrusion?.below_ground_depth_m  || 0;
  const scale = scaleFp || scaleTr || 0.035;

  const { cx, cy, radius } = useMemo(() => {
    const all = [...footprintPx, ...terrainPx];
    if (all.length === 0) return { cx: 0, cy: 0, radius: 20 };
    const xs = all.map(([x]) => x);
    const ys = all.map(([, y]) => y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    return {
      cx: (minX + maxX) / 2,
      cy: (minY + maxY) / 2,
      radius: Math.max(10, Math.max((maxX - minX) * scale, (maxY - minY) * scale, aboveH) * 1.4),
    };
  }, [footprintPx, terrainPx, scale, aboveH]);

  const footprintShape = useMemo(() => polyToShape2D(
    footprintPx.map(([x, y]) => [(x - cx) * scale, (y - cy) * scale])
  ), [footprintPx, cx, cy, scale]);
  const terrainShape = useMemo(() => polyToShape2D(
    terrainPx.map(([x, y]) => [(x - cx) * scale, (y - cy) * scale])
  ), [terrainPx, cx, cy, scale]);

  const target = useMemo(() => [0, aboveH / 2, 0], [aboveH]);

  const empty = footprintPx.length === 0 && terrainPx.length === 0;

  const applyPresetToCam = (key) => {
    const preset = VIEW_PRESETS[key];
    if (!preset || !stateRef.current) return;
    const { camera, controls } = stateRef.current;
    if (!camera || !controls) return;
    const az = (preset.az * Math.PI) / 180;
    const el = (preset.el * Math.PI) / 180;
    const r = radius;
    const x = target[0] + r * Math.cos(el) * Math.sin(az);
    const y = target[1] + r * Math.sin(el);
    const z = target[2] + r * Math.cos(el) * Math.cos(az);
    camera.position.set(x, y, z);
    controls.target.set(...target);
    camera.lookAt(...target);
    camera.updateProjectionMatrix();
    controls.update();
  };

  const { canvasRef, stateRef: hookStateRef } = useThreeViewer({
    background: '#0b1220',
    cameraInit: {
      pos: [radius, radius * 0.7, radius],
      target,
    },
    enableShadows: true,
    deps: [footprintShape, terrainShape, aboveH, belowH, radius],
    onCreate: ({ scene, camera, controls, renderer }) => {
      if (empty) return undefined;
      stateRef.current = { scene, camera, controls, renderer };
      applySkyBackground(scene);
      const root = buildExterior({ footprintShape, terrainShape, aboveH, belowH, radius });

      const ambient = new THREE.AmbientLight(0xffffff, 0.45);
      const dir = new THREE.DirectionalLight(0xffffff, 1.3);
      dir.position.set(60, 80, 40);
      dir.castShadow = true;
      if (dir.shadow) {
        dir.shadow.mapSize.width = 2048;
        dir.shadow.mapSize.height = 2048;
        dir.shadow.camera.left = -radius;
        dir.shadow.camera.right = radius;
        dir.shadow.camera.top = radius;
        dir.shadow.camera.bottom = -radius;
        dir.shadow.camera.far = radius * 4;
      }
      const hemi = new THREE.HemisphereLight(0xffeedd, 0x223344, 0.4);
      root.add(ambient);
      root.add(dir);
      root.add(hemi);

      const grid = new THREE.GridHelper(radius * 4, Math.max(40, Math.floor(radius * 4)),
        0x374151, 0x1f2937);
      grid.position.y = -0.04;
      root.add(grid);

      scene.add(root);

      // Apply initial preset.
      const preset = VIEW_PRESETS[presetKey];
      if (preset) {
        const az = (preset.az * Math.PI) / 180;
        const el = (preset.el * Math.PI) / 180;
        camera.position.set(
          target[0] + radius * Math.cos(el) * Math.sin(az),
          target[1] + radius * Math.sin(el),
          target[2] + radius * Math.cos(el) * Math.cos(az),
        );
        controls.target.set(...target);
        camera.lookAt(...target);
        camera.updateProjectionMatrix();
        controls.update();
      }

      return () => disposeGroup(root);
    },
  });

  // Sync the imperative cam controls with the hook state ref when ready.
  if (hookStateRef && hookStateRef.current && hookStateRef.current.controls) {
    stateRef.current = hookStateRef.current;
  }

  const handleCapture = () => {
    if (!stateRef.current?.renderer) {
      toast.error('Canvas 3D non prêt');
      return;
    }
    setCapturing(true);
    try {
      // Force one final render before reading the buffer.
      const { renderer, scene, camera } = stateRef.current;
      renderer.render(scene, camera);
      const dataUrl = renderer.domElement.toDataURL('image/png');
      if (onCapture) onCapture(dataUrl);
      else {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = `exterior-${Date.now()}.png`;
        a.click();
        toast.success('Vue capturée — téléchargement lancé');
      }
    } catch (e) {
      toast.error('Capture impossible');
    } finally {
      setCapturing(false);
    }
  };

  const handlePreset = (key) => {
    setPresetKey(key);
    applyPresetToCam(key);
  };

  if (empty) {
    return (
      <div className={`flex flex-col items-center justify-center text-gray-400 text-[12px] gap-2 ${className}`}
           style={{ minHeight: 320, background: '#0b1220' }}
           data-testid={testId}>
        <Mountain className="w-6 h-6 opacity-60" />
        <span>Aucune empreinte ni terrain — dessinez des murs au RDC puis tracez le terrain pour activer la vue 3D.</span>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}
         style={{ background: '#0b1220' }} data-testid={testId}>
      {/* Overlay header */}
      <div className="absolute top-3 left-3 z-10 pointer-events-none">
        <div className="text-[10.5px] uppercase tracking-[0.25em] text-emerald-300/80">
          {title}
        </div>
        {subtitle && <div className="text-[11.5px] text-gray-300 mt-0.5">{subtitle}</div>}
      </div>

      <div className="absolute top-3 right-3 z-10 flex flex-wrap gap-1 max-w-[60%] justify-end">
        {Object.entries(VIEW_PRESETS).map(([k, p]) => (
          <button key={k} type="button"
                  onClick={() => handlePreset(k)}
                  data-testid={`exterior-3d-preset-${k}`}
                  className={`h-7 px-2 rounded text-[10.5px] border transition-colors ${
                    presetKey === k
                      ? 'bg-emerald-500/30 border-emerald-400/70 text-emerald-100'
                      : 'bg-black/40 border-white/15 text-gray-200 hover:bg-white/5'
                  }`}
                  title={p.label}>
            <Compass className="w-3 h-3 inline-block mr-1" />
            {p.label}
          </button>
        ))}
      </div>

      <div className="absolute bottom-3 right-3 z-10 flex gap-1.5">
        <button type="button"
                onClick={() => handlePreset(presetKey)}
                data-testid="exterior-3d-reset-cam"
                className="h-7 px-2 rounded text-[10.5px] border border-white/15 bg-black/40 text-gray-200 hover:bg-white/5"
                title="Recentrer la caméra sur le preset courant">
          <RotateCcw className="w-3 h-3" />
        </button>
        <button type="button"
                onClick={handleCapture}
                disabled={capturing}
                data-testid="exterior-3d-capture-btn"
                className="h-7 px-2.5 rounded text-[10.5px] font-semibold border border-emerald-400/70 bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30 inline-flex items-center gap-1.5">
          {capturing
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <CameraIcon className="w-3.5 h-3.5" />}
          {capturing ? 'Capture…' : 'Capturer la vue'}
        </button>
      </div>

      <canvas ref={canvasRef}
              className="block w-full h-full"
              data-testid={`${testId}-canvas`} />
    </div>
  );
};

export default Exterior3DViewer;
